// Pure state/gate/payload logic for the admin scheduler — no Vue, no API calls,
// so the booking rules stay unit-testable (frontend/test/sch-admin/logic.spec.js).
import { ADMIN_PRODUCTS, COPY } from './copy'
import { agentTakesTier } from '~/util/advisor-tiers'
import { isOpenState, openStateMethods, dpcMethods } from '~/util/open-states'

export const SC_KEYS = ['hsa', 'collecting_ssi', 'moving', 'multiple_states']
export const PRODUCT_KEYS = ADMIN_PRODUCTS.map(p => p.value)
const PRODUCT_LABELS = ADMIN_PRODUCTS.reduce((m, p) => { m[p.value] = p.label; return m }, {})

export function newSoa () {
    // Every product starts on Yes. Legal (Leah Luo, 2026-09-24): "Have every
    // plan checked by default on the scope and they can uncheck if wanted" --
    // the agent reads the scope script and moves what the client declines to
    // No. It used to start every product unanswered.
    // snake_case: mirrors the SignedSoaForm field persisted to the backend
    // eslint-disable-next-line camelcase
    const product_answers = {}
    PRODUCT_KEYS.forEach((k) => { product_answers[k] = 'yes' })
    // attempted: admin tried to capture with gaps → amber the unanswered rows
    // representative*: a POA / legal guardian consented for the attendee (#605)
    return {
        product_answers,
        captured: false,
        captured_at: null,
        absent: false,
        attempted: false,
        representative: false,
        representative_name: '',
        representative_relationship: '',
    }
}

export function newPerson (primary = false) {
    return {
        primary,
        // Tri-state pills (advisor feedback): null = unanswered — the admin
        // must explicitly pick before booking (see bookingGates). The primary
        // always needs their own guidance/SOA, so only attendees start null.
        returning: null,
        guidance: primary ? true : null,
        record: null, // full survey object once a CRM result is picked
        first_name: '',
        middle_name: '',
        last_name: '',
        dob: null,
        gender: null,
        group_relationship: null,
        relationship: null,
        // Per-attendee referral (new + needs-guidance attendees). Mirrors the
        // primary's state.referral so a spouse under a different employer group
        // can be attributed correctly. Unused for the primary (state.referral).
        source_type: null,
        source_name: null,
        group: null,
        groupPaying: false,
        groupFinancialAdvisor: false,
        groupDpc: false,
        // A former Benefit Bay (ICHRA) employer, from the picked group's config.
        groupFormerIchra: false,
        email: '',
        phone: '',
        sms_opt_in: true,
        // #680 Phase 2a: full street address, collected alongside ZIP
        // (ClientInfoCard / ReturningClientFields). `state` below already
        // existed (the zip-county lookup writes it); street_1/street_2/city
        // are new.
        street_1: '',
        street_2: '',
        city: '',
        zip_code: '',
        fips: null,
        county: null,
        counties: [],
        state: null,
        effective_date: null,
        medicare_parts: null,
        hsa: null,
        collecting_ssi: null,
        moving: null,
        moving_state: '',
        multiple_states: null,
        scAttempted: false, // book attempt with unanswered SC → amber rows
        soa: newSoa(),
    }
}

// Paying-source rule (mirrors flow: get_group_allowed_methods / User.is_fullserve):
// a client may only book a phone/video consult if they're fullserve. For a NEW
// client that's decided by their referral source + selected group's flags.
// Returns null when undetermined (source or paying-group not chosen yet), []
// when determined non-paying (blocked), or the allowed methods otherwise.
// Sources that carry an employer/practice group (and therefore a group
// relationship). Employer group + group relationship are required for these.
export const GROUP_SOURCES = ['EMPLOYER', 'FINANCIAL_ADVISOR', 'DIRECT_PRIMARY_CARE']

export function groupBasedReferral (referral) {
    return !!referral && GROUP_SOURCES.includes(referral.source_type)
}

// Which of the group boxes a referral source actually asks for (#605).
// Doctor's Choice Internal referrals carry no employer/practice group at all.
// Financial-advisor and DPC referrals do carry a group, but "relationship to the
// group" (Employee, Spouse of employee…) is an employer-only concept.
export const NO_GROUP_SOURCES = ['DOCTOR_CHOICE_INTERNAL', 'OTHER']
// #727 (2026-10-05 request): the Other source's state box offers only the open
// states plus this marker for "somewhere else". Never a real state: it is not
// open, so the client is blocked, and it is never copied onto the client.
export const OTHER_STATE = 'OTHER'
export const NO_GROUP_RELATIONSHIP_SOURCES = ['FINANCIAL_ADVISOR', 'DIRECT_PRIMARY_CARE', 'DOCTOR_CHOICE_INTERNAL', 'OTHER']

// #727: Other asks for the client's state instead of a group.
// #730: how a new client's referral resolves. 'paying' (full methods), 'dpc',
// 'former_ichra' (blocked, with the Benefit Bay message), 'non_paying' (judged
// on the client's state: FL/RI books, anywhere else is blocked), or null while
// undetermined (no source yet, or a group source with no group picked).
// Family Member and the legacy sources are never paying, whatever group is
// picked (as before #730).
export function referralTier (referral) {
    const st = referral && referral.source_type
    if (!st) return null
    if (st === 'DOCTOR_CHOICE_INTERNAL') return 'paying'
    if (GROUP_SOURCES.includes(st)) {
        if (!referral.group) return null
        if (referral.groupFormerIchra) return 'former_ichra'
        if (referral.groupPaying || referral.groupFinancialAdvisor) return 'paying'
        if (referral.groupDpc) return 'dpc'
        return 'non_paying'
    }
    if (referral.groupFormerIchra) return 'former_ichra'
    return 'non_paying'
}

// #727/#730: the "Client's state" box -- asked whenever the referral has
// resolved to non-paying, since the state then decides eligibility.
export function showClientState (referral) {
    return referralTier(referral) === 'non_paying'
}

export function showReferralGroup (referral) {
    return !!referral && !NO_GROUP_SOURCES.includes(referral.source_type)
}

export function showGroupRelationship (referral) {
    return !!referral && !NO_GROUP_RELATIONSHIP_SOURCES.includes(referral.source_type)
}

// Required only where the box is both asked for and part of a group-based
// referral — i.e. employer groups. Never gate on a hidden field.
export function needsGroupRelationship (referral) {
    return groupBasedReferral(referral) && showGroupRelationship(referral)
}

// #679: `aep` mirrors the exact branch in
// flow/scheduling/endpoints/client_token.py::get_group_allowed_methods —
// once *some* methods are allowed, aep strips 'video', always adds 'survey',
// and ALSO strips 'email'/'phone' unless the source is exempt (group.paying,
// group.financial_advisor, or DOCTOR_CHOICE_INTERNAL). A new client has no
// `user`, so the backend's "not user" half of that exemption check is always
// true here — only the group flags (or DC-internal, which carries no group)
// decide the exemption.
export function newClientAllowedMethods (referral, aep = false, openStates = []) {
    const tier = referralTier(referral)
    if (tier === null) return null
    if (tier === 'paying') return aep ? ['email', 'phone', 'survey'] : ['email', 'phone', 'video']
    // 2026-10-02 (Dien): DPC is phone-only; the call survives AEP.
    if (tier === 'dpc') return dpcMethods(aep)
    // A former Benefit Bay employer stays blocked, in an open state too (Aziz 2026-10-05).
    if (tier === 'former_ichra') return []
    // #727/#730: non-paying is undetermined until a state is chosen; open
    // states get phone (phone + survey in AEP); anywhere else is blocked.
    if (!referral.state) return null
    return isOpenState(referral.state, openStates) ? openStateMethods(aep) : []
}

export function newSchedulerState () {
    return {
        primary: newPerson(true),
        // group* flags mirror the picked group's paying/financial_advisor/dpc
        // booleans (GroupSelect input-raw) so newClientAllowedMethods can decide
        // eligibility without another round-trip.
        referral: {
            source_type: null,
            source_name: null,
            group: null,
            groupPaying: false,
            groupFinancialAdvisor: false,
            groupDpc: false,
            groupFormerIchra: false,
            // #727 Other-source referral
            state: null,
            referred_by_type: null, // 'CLIENT' | 'EMPLOYER' | 'OTHER'
            referred_by_client_id: null,
            referred_by_client_name: '',
            referred_by_employer_name: '',
            referred_by_employer_contact: '',
            sales_followup: true,
            referred_by_note: '',
        },
        attendees: [],
        schedule: {
            // null = unanswered (advisor feedback) — the admin picks the
            // method; the orchestrator seeds it from ?method= when present.
            method: null,
            timezone: 'America/New_York',
            force_agent: null,
            date: null,
            time: null,
            agent: null,
            agents: null,
            aep: null,
            // Consultation type under aep — 'new_to_medicare' | 'plan_review'
            // (MethodTimeCard's consultation-type pills, Task 3). Required to
            // book whenever aep is true (see bookingGates); sent to /book/ as
            // `reason` (BookForm.reason), and the backend stamps
            // reason_source='agent' for staff bookings.
            reason: null,
        },
        notes: '',
    }
}

// What a kept draft (util/scheduler-draft.js) holds of a picked CRM record: the
// fields the scheduler reads, nothing more. The record is the client's whole
// survey, Medicare number and health answers included, and browsers write
// sessionStorage to disk for session restore.
export const DRAFT_RECORD_KEYS = [
    'id', 'first_name', 'last_name', 'dob', 'gender', 'email', 'work_email', 'cell_phone', 'phone',
    'medicare_parts', 'effective_date', 'street_1', 'street_2', 'city', 'state', 'zip_code', 'fips', 'county', 'agent',
]

export function draftRecord (record) {
    if (!record) return record
    return DRAFT_RECORD_KEYS.reduce((kept, k) => {
        if (record[k] !== undefined) kept[k] = record[k]
        return kept
    }, {})
}

export function draftSchedulerState (state) {
    const person = p => ({ ...p, record: draftRecord(p.record) })
    return { ...state, primary: person(state.primary), attendees: state.attendees.map(person) }
}

// A draft brought back after the page was left (util/scheduler-draft.js), on
// today's shape in case a deploy landed in between. The picked slot is dropped:
// its hold went with the page, and someone else may have the time now.
export function restoreSchedulerState (saved) {
    const fresh = newSchedulerState()
    const person = (p, primary) => {
        const base = newPerson(primary)
        return { ...base, ...p, soa: { ...base.soa, ...(p && p.soa) } }
    }
    const state = {
        ...fresh,
        ...saved,
        primary: person(saved.primary, true),
        referral: { ...fresh.referral, ...saved.referral },
        attendees: (saved.attendees || []).map(a => person(a, false)),
        schedule: { ...fresh.schedule, ...saved.schedule, date: null, time: null, agent: null, agents: null },
    }
    return { state, slotDropped: !!(saved.schedule && saved.schedule.time) }
}

// Copy the editable "on file" fields from a picked CRM record onto a person so
// the returning-client fields prefill and the admin can correct them. Identity
// (name/DOB/gender/email) is handled by the pick handlers; this covers the
// fields ReturningClientFields edits and toClientEntry sends back. Keys already
// exist on newPerson(), so plain assignment stays reactive.
export function fillFromRecord (p, record) {
    if (!record) return
    // Email is read-only on file, but still shown — populate it so the returning
    // client's email box (and the carried-over note) aren't blank. Prefer the
    // account email, falling back to work_email.
    p.email = record.email || record.work_email || p.email || ''
    p.phone = record.cell_phone || record.phone || p.phone || ''
    p.medicare_parts = record.medicare_parts || null
    p.zip_code = record.zip_code || ''
    p.fips = record.fips || null
    p.state = record.state || null
    p.county = record.county || null
    p.counties = []
    // #680 Phase 2a: prefill the on-file address too, same as zip/phone above
    // — staff correcting a returning client should see what's on file, not a
    // blank street/city, and toClientEntry's only-set-when-provided
    // corrections semantics already tolerate this (an unedited value is sent
    // back and the backend no-ops when nothing changed).
    p.street_1 = record.street_1 || ''
    p.street_2 = record.street_2 || ''
    p.city = record.city || ''
    // Coverage date is deliberately NOT prefilled (Kalena/Dien, 2026-09-15).
    // What's on file is usually the date from the client's first consult or
    // their original Medicare enrollment, so prefilling it just puts a stale
    // (often past) date in front of the admin to re-confirm. The admin enters
    // the date this booking is about, from scratch, every time. It's cleared
    // rather than left alone so re-picking a different record can't carry the
    // previous person's date over. Left blank, toClientEntry omits the key and
    // book.py leaves the account's stored date untouched (Dien: keep the old
    // value even when it's in the past; the advisor updates it on the call).
    p.effective_date = null
}

export function personName (p) {
    if (p.returning && p.record) {
        return `${p.record.first_name || ''} ${p.record.last_name || ''}`.trim()
    }
    const name = `${p.first_name || ''} ${p.last_name || ''}`.trim()
    return name || (p.primary ? 'Primary client' : 'Attendee')
}

export function eligiblePeople (state) {
    return [state.primary].concat(state.attendees.filter(a => a.guidance))
}

export function soaInScope (soa) {
    return PRODUCT_KEYS.filter(k => soa.product_answers[k] === 'yes' || soa.product_answers[k] === 'unsure')
}

export function soaUnanswered (soa) {
    return PRODUCT_KEYS.filter(k => !soa.product_answers[k])
}

export function soaReady (soa) {
    return !soaUnanswered(soa).length && soaInScope(soa).length > 0
}

// Fill text for the confirmation script, e.g.
// "Medicare Advantage (Part C), Dental / Vision / Hearing". Bare product names
// only — the "(advisor will explain)" marker that used to trail an "unsure"
// answer was removed so the script reads cleanly verbatim (#605). "Not sure"
// still counts as in scope.
export function soaProductNames (soa) {
    const names = soaInScope(soa).map(k => PRODUCT_LABELS[k])
    return names.join(', ') || COPY.s4.productsPlaceholder
}

// A flagged authorized representative needs both boxes filled before the verbal
// SOA can be captured — they're the two fields the SOA's rep section wants.
export function soaRepReady (soa) {
    if (!soa.representative) return true
    return !!(soa.representative_name || '').trim() && !!(soa.representative_relationship || '').trim()
}

export function unansweredSc (p) {
    return SC_KEYS.filter(k => p[k] === null)
}

// Cheap format guards so typos surface during the call instead of as raw
// backend validation errors at book time.
export function validEmail (v) {
    return /^\S+@\S+\.\S+$/.test((v || '').trim())
}

export function validPhone (v) {
    return ((v || '').replace(/\D/g, '')).length >= 10
}

// Ordered booking gates — ticket order (1-5), then hard backend prerequisites.
// Empty array = bookable. The first entry drives the Book-button message.
export function bookingGates (state, { aepSeason = false, openStates = [] } = {}) {
    const gates = []
    const { primary, attendees, schedule } = state

    // Advisor feedback: no defaults — the routing questions and consultation
    // method must be answered explicitly before anything else.
    if (schedule.method == null) {
        gates.push({ key: 'method', message: COPY.gates.method })
    }
    // #679 review Finding 1 (critical) fix (b): the suspenders to
    // MethodTimeCard.setAep's belt (which structurally clears a pre-selected
    // 'video' the instant schedule.aep flips true, independent of the
    // returningAllowedMethods async fetch — see MethodTimeCard.spec.js). This
    // is a defensive booking-time guard: video is never offered under aep, so
    // even if some future path ever sets schedule.method = 'video' directly
    // without going through setMethod/setAep, booking still can't proceed.
    if (schedule.aep && schedule.method === 'video') {
        gates.push({ key: 'video_aep', message: COPY.gates.videoAepBlocked })
    }
    // #679 review fold-in minor #3(b) (phase review): the mirror gate, same
    // rationale in the other direction — survey only exists as a consult
    // method under aep, so a stale schedule.method === 'survey' surviving
    // aep being turned off (MethodTimeCard.setAep's minor #3(a) clear is the
    // belt) is still caught here at booking time.
    if (!schedule.aep && schedule.method === 'survey') {
        gates.push({ key: 'survey_non_aep', message: COPY.gates.surveyNonAepBlocked })
    }
    // Consultation type (New to Medicare / AEP plan review) is required to
    // book all season, regardless of method — it IS the aep answer now
    // (Dien 2026-09-01; the old Yes/No pill is gone), and it maps to
    // `reason` on the book payload (backend stamps reason_source='agent').
    // The schedule.aep half stays as a belt for callers that set aep
    // without the pill (presets, tests).
    if ((aepSeason || schedule.aep) && !schedule.reason) {
        gates.push({ key: 'reason', message: COPY.gates.reason })
    }
    if ((schedule.method === 'phone' || schedule.method === 'video') && (!schedule.date || !schedule.time)) {
        gates.push({ key: 'time', message: COPY.gates.time })
    }
    if (primary.returning === false && unansweredSc(primary).length) {
        gates.push({ key: 'client_sc', message: COPY.gates.clientSc(unansweredSc(primary).length) })
    }
    // The referral question is asked on the Other path only (#727); every other
    // non-paying client in an open state has their referral already (#730).
    const ref = state.referral
    if (primary.returning === false && ref.source_type === 'OTHER' && isOpenState(ref.state, openStates)) {
        if (!ref.referred_by_type) {
            gates.push({ key: 'referral_type', message: COPY.gates.referralType })
        } else if (ref.referred_by_type === 'EMPLOYER' && !(ref.referred_by_employer_name || '').trim()) {
            gates.push({ key: 'referral_employer', message: COPY.gates.referralEmployer })
        }
    }
    attendees.forEach((a) => {
        if (a.returning === null || a.guidance === null) {
            gates.push({ key: 'attendee_status', message: COPY.gates.attendeeStatus(personName(a)) })
        }
    })
    attendees.forEach((a) => {
        if (a.guidance === true && (!validEmail(a.email) || !validPhone(a.phone))) {
            gates.push({ key: 'attendee_contact', message: COPY.gates.attendeeContact(personName(a)) })
        }
    })
    attendees.forEach((a) => {
        if (a.guidance === true && a.returning === false && !a.source_type) {
            gates.push({ key: 'attendee_referral', message: COPY.gates.attendeeReferral(personName(a)) })
        }
    })
    // Group-based attendees need an employer group + relationship. Unlike the
    // primary, attendees have no paying-eligibility gate, so enforce both here.
    attendees.forEach((a) => {
        if (a.guidance === true && a.returning === false && groupBasedReferral(a) && !a.group) {
            gates.push({ key: 'attendee_group', message: COPY.gates.attendeeGroup(personName(a)) })
        }
    })
    attendees.forEach((a) => {
        if (a.guidance === true && a.returning === false && needsGroupRelationship(a) && !a.group_relationship) {
            gates.push({ key: 'attendee_group_relationship', message: COPY.gates.attendeeGroupRelationship(personName(a)) })
        }
    })
    attendees.forEach((a) => {
        if (a.guidance === true && a.returning === false && unansweredSc(a).length) {
            gates.push({ key: 'attendee_sc', message: COPY.gates.attendeeSc(personName(a), unansweredSc(a).length) })
        }
    })
    if (eligiblePeople(state).some(p => !p.soa.captured && !p.soa.absent)) {
        gates.push({ key: 'soa_pending', message: COPY.gates.soaPending })
    }

    // Backend prerequisites outside the ticket's ordered list
    if (primary.returning === true) {
        if (!primary.record) {
            gates.push({ key: 'lookup', message: COPY.gates.lookup })
        } else if (!primary.record.email) {
            // book.py hard-rejects existing clients without an email on file
            gates.push({ key: 'returning_email', message: COPY.gates.returningEmail })
        }
    } else if (primary.returning === false) {
        // #680 Phase 2a: street/city/state join the same identity/contact
        // gate as name/dob/gender/email/phone — same required-field bucket
        // the card marks with `.req`, no new gate invented. ZIP keeps its own
        // gates below (it resolves to fips via a lookup, unlike these plain
        // strings).
        if (!(primary.first_name || '').trim() || !(primary.last_name || '').trim() ||
            !validEmail(primary.email) || !validPhone(primary.phone) ||
            !primary.dob || !primary.gender ||
            !(primary.street_1 || '').trim() || !(primary.city || '').trim() || !primary.state) {
            gates.push({ key: 'client_fields', message: COPY.gates.clientFields })
        }
        if (!state.referral.source_type) {
            gates.push({ key: 'referral', message: COPY.gates.referral })
        }
        // Group relationship is asked for (and required) only on employer-group
        // referrals — FA / DPC / DC-internal hide the box entirely (#605). The
        // employer group itself is required too, but for the primary that's
        // already enforced by the paying-source eligibility gate (you can't reach
        // booking without a paying group).
        if (needsGroupRelationship(state.referral) && !primary.group_relationship) {
            gates.push({ key: 'group_relationship', message: COPY.gates.groupRelationship })
        }
        if (!primary.zip_code) {
            gates.push({ key: 'zip', message: COPY.gates.zip })
        } else if (!primary.fips) {
            // multi-county ZIP not confirmed yet — agents pool + quoting need fips
            gates.push({ key: 'county', message: COPY.gates.county })
        }
    } else {
        gates.push({ key: 'status', message: COPY.gates.status })
    }
    return gates
}

export function toSoaPayload (p) {
    if (!p.soa.captured) return null
    const products = {}
    PRODUCT_KEYS.forEach((k) => {
        products[k] = p.soa.product_answers[k] === 'yes' || p.soa.product_answers[k] === 'unsure'
    })
    const payload = {
        full_name: personName(p),
        products,
        product_answers: { ...p.soa.product_answers },
        role: 'verbal',
        captured_at: p.soa.captured_at,
    }
    // #680 final-review wave (Fix 3): mirrors the client wizard's Fix 1 —
    // require the attendee's own street before building the address string,
    // so a returning client who hasn't been prefilled (only zip on file,
    // e.g. a lookup that never carried a street) never sends a bare-zip
    // partial address that the server's `value.get('address') or
    // user.address` (flow/consults/soa.py:266) would incorrectly prefer over
    // the richer user.address on file. Admin has no guest-inheritance case,
    // so this is simpler than the wizard's rule: own street present → own
    // four fields joined; otherwise the key is omitted entirely so the
    // server's user.address fallback applies.
    if ((p.street_1 || '').trim()) {
        payload.address = [p.street_1, p.city, p.state, p.zip_code].filter(Boolean).join(', ')
    }
    // An authorized representative consented on the attendee's behalf (#605):
    // full_name stays the beneficiary, and the rep's name both fills the SOA's
    // authorized-rep box and signs it (typed_name — see
    // flow.consults.soa.soa_authorized_representative).
    if (p.soa.representative) {
        const name = (p.soa.representative_name || '').trim()
        payload.typed_name = name
        payload.representative_name = name
        payload.representative_relationship = (p.soa.representative_relationship || '').trim()
    }
    return payload
}

// #727: the Booking summary's "Referred by" row.
export function referredByLabel (referral) {
    const r = referral || {}
    if (!r.referred_by_type) return COPY.rail.referredByPending
    if (r.referred_by_type === 'CLIENT') {
        const name = (r.referred_by_client_name || '').trim()
        if (!name) return COPY.rail.referredByClient
        return r.referred_by_client_id ? `Client: ${name} (linked)` : `Client: ${name}`
    }
    if (r.referred_by_type === 'EMPLOYER') {
        const name = (r.referred_by_employer_name || '').trim()
        if (!name) return COPY.rail.referredByEmployerPending
        return r.sales_followup ? `Employer: ${name} · sales flag` : `Employer: ${name}`
    }
    const note = (r.referred_by_note || '').trim()
    return note ? `Other: ${note}` : 'Other'
}

export function toClientEntry (p, referral = null, { openStates = [] } = {}) {
    const soa = toSoaPayload(p)
    if (p.returning && p.record) {
        const entry = { id: p.record.id }
        // Editable "on file" corrections (ReturningClientFields). book.py
        // persists these onto the existing account; identity stays read-only
        // and is intentionally omitted. ZIP rides along only with a confirmed
        // county (fips), so a stored ZIP never disagrees with its fips/state.
        const phone = (p.phone || '').trim()
        if (phone) entry.phone = phone
        if (p.medicare_parts) entry.medicare_parts = p.medicare_parts
        if (p.effective_date) entry.effective_date = p.effective_date
        if (p.zip_code && p.fips) {
            entry.zip_code = p.zip_code
            entry.fips = p.fips
        }
        // #680 Phase 2a: full address corrections. Unlike zip/fips these are
        // independent strings (no pairing constraint), so each rides along
        // on its own whenever the admin has entered it.
        const street1 = (p.street_1 || '').trim()
        if (street1) entry.street_1 = street1
        const street2 = (p.street_2 || '').trim()
        if (street2) entry.street_2 = street2
        const city = (p.city || '').trim()
        if (city) entry.city = city
        if (p.state) entry.state = p.state
        if (soa) entry.soa = soa
        return entry
    }
    const entry = {
        first_name: (p.first_name || '').trim(),
        middle_name: (p.middle_name || '').trim() || null,
        last_name: (p.last_name || '').trim(),
        email: (p.email || '').trim(),
        phone: (p.phone || '').trim() || null,
        sms_opt_in: !!p.sms_opt_in,
        dob: p.dob || null,
        gender: p.gender || null,
        zip_code: p.zip_code || null,
        fips: p.fips || null,
        // #680 Phase 2a: full address, alongside ZIP/fips above.
        street_1: p.street_1 || null,
        street_2: p.street_2 || null,
        city: p.city || null,
        state: p.state || null,
        effective_date: p.effective_date || null,
        medicare_parts: p.medicare_parts || null,
        group_relationship: p.group_relationship || null,
        // null = unanswered; the gates guarantee answered where required
        hsa: !!p.hsa,
        collecting_ssi: !!p.collecting_ssi,
        moving: !!p.moving,
        moving_state: p.moving ? (p.moving_state || null) : null,
        multiple_states: !!p.multiple_states,
    }
    if (referral) {
        entry.source_type = referral.source_type || null
        entry.source_name = referral.source_name || null
        entry.group = referral.group || null
    }
    // #727/#730: a new client the open-state rule governs carries the step-1
    // state; an Other-source one also carries who referred them.
    if (referral && showClientState(referral) && isOpenState(referral.state, openStates)) {
        entry.state = referral.state
        if (referral.source_type === 'OTHER') {
            if (referral.referred_by_client_id) entry.source_client = referral.referred_by_client_id
            const t = referral.referred_by_type
            entry.referral = {
                type: t,
                client_name: t === 'CLIENT' ? ((referral.referred_by_client_name || '').trim() || null) : null,
                employer_name: t === 'EMPLOYER' ? ((referral.referred_by_employer_name || '').trim() || null) : null,
                employer_contact: t === 'EMPLOYER' ? ((referral.referred_by_employer_contact || '').trim() || null) : null,
                sales_followup: t === 'EMPLOYER' ? !!referral.sales_followup : false,
                note: t === 'OTHER' ? ((referral.referred_by_note || '').trim() || null) : null,
            }
        }
    }
    if (!p.primary) entry.relationship = p.relationship || null
    if (soa) entry.soa = soa
    return entry
}

export function buildBookBody (state, { agentId = null, sessionId = null, openStates = [] } = {}) {
    const clients = [toClientEntry(state.primary, state.referral, { openStates })]
    const caretakers = []
    state.attendees.forEach((a) => {
        // Strict booleans: an unanswered guidance pill (null) must never
        // silently book as sitting-in — the attendee_status gate blocks it.
        if (a.guidance === true) {
            // New guidance attendees carry their own referral; returning ones
            // ignore it (toClientEntry returns the bare {id} entry).
            clients.push(toClientEntry(a, { source_type: a.source_type, source_name: a.source_name, group: a.group }, { openStates }))
        } else if (a.guidance === false && (a.first_name || '').trim() && (a.last_name || '').trim()) {
            // Caretaker schema requires first+last — nameless cards are omitted.
            caretakers.push({
                first_name: a.first_name.trim(),
                last_name: a.last_name.trim(),
                email: (a.email || '').trim() || null,
                phone: (a.phone || '').trim() || null,
                relationship: a.relationship || null,
            })
        }
    })
    // Email and survey are both "asynchronous" methods: no calendar slot, no
    // held session — an advisor (or the AEP survey link) follows up later, so
    // both use the passed-in agentId pick (the same email-schedulable agent
    // pool as before) rather than a booked slot's agent (mirrors the client
    // wizard's StepByStepV2.isAsyncMethod).
    const asyncMode = state.schedule.method === 'email' || state.schedule.method === 'survey'
    const body = {
        clients,
        caretakers,
        method: state.schedule.method,
        timezone: state.schedule.timezone,
        aep: !!state.schedule.aep,
        // #679: the consultation-type pill's answer, sent whenever answered —
        // including new_to_medicare, which books as a normal (aep: false)
        // consultation in season (Dien 2026-09-01). `undefined` is dropped
        // by the JSON serializer, so an off-season payload stays
        // byte-identical to today (mirrors StepByStepV2.book's `reason`).
        reason: state.schedule.reason || undefined,
        notes: state.notes || '',
        agent: asyncMode ? agentId : state.schedule.agent,
        skip_survey: false,
    }
    if (!asyncMode) {
        body.time = state.schedule.time
        if (sessionId) body.session_id = sessionId
    }
    return body
}

// Payer / non-payer advisor routing for phone and video (Dien 2026-10-15,
// Aziz 2026-10-16). A new client's tier, judged the way /book/ will judge it
// (flow/scheduling/advisor_tiers.client_is_payer): Doctor's Choice Internal, or
// a paying / financial-advisor group, is a payer; everyone else a non-payer.
// toClientEntry sends referral.group for every source, so a Family Member
// picked with a paying employer is a payer here too. null until the referral
// says anything. A returning client's tier comes from /client-allowed-methods/.
export function payerFor (referral) {
    if (!referral) return null
    if (referral.source_type === 'DOCTOR_CHOICE_INTERNAL') return true
    if (referral.group) return !!(referral.groupPaying || referral.groupFinancialAdvisor)
    return referral.source_type ? false : null
}

// Whether an advisor meets this tier: shared with the client wizard.
export { agentTakesTier }
